import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Hotel, Loader2, RefreshCw, FlaskConical, Ban } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** Admin: RateHawk booking capability + ETG certification test-hotel bookings (must be cancelled after ETG checks them). */
export const EtgCertificationCard = () => {
  const [cap, setCap] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState('');
  const load = async () => {
    try {
      const [c, r] = await Promise.all([axios.get(`${API_URL}/api/admin/umrah/capabilities`, cfg), axios.get(`${API_URL}/api/admin/umrah/etg/test-bookings`, cfg)]);
      setCap(c.data); setRuns(r.data || []);
    } catch { /* admin only */ }
  };
  useEffect(() => { load(); }, []);
  const recheck = async () => {
    setBusy('recheck');
    try { const { data } = await axios.post(`${API_URL}/api/admin/umrah/capabilities/ratehawk/recheck`, {}, cfg); toast[data.ok ? 'success' : 'error'](data.ok ? 'RateHawk booking enabled — package payments open' : `Still disabled: ${data.error}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Re-check failed'); } finally { setBusy(''); }
  };
  const testBooking = async () => {
    if (!window.confirm('Create ETG certification test bookings at test_hotel_do_not_book (2 adults + child 5 / 2 adults, residency uz)? They must be cancelled afterwards.')) return;
    setBusy('test');
    try { const { data } = await axios.post(`${API_URL}/api/admin/umrah/etg/test-booking`, { confirm: true }, cfg); toast.success(`Test run ${data.run_id} created`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test booking failed'); } finally { setBusy(''); }
  };
  const cancelRun = async (runId) => {
    setBusy(runId);
    try { await axios.post(`${API_URL}/api/admin/umrah/etg/test-booking/${runId}/cancel`, {}, cfg); toast.success('Test bookings cancelled'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); } finally { setBusy(''); }
  };
  const enabled = cap?.ratehawk_booking?.enabled;
  return (
    <Card data-testid="etg-certification-card">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Hotel className="h-4 w-4 text-[#FF6600]" />RateHawk (ETG) booking & certification</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} data-testid="etg-capability-badge">{enabled ? 'Booking enabled' : 'Booking disabled'}</Badge>
          <span className="text-xs text-slate-500">checked {when(cap?.ratehawk_booking?.checked_at)}{cap?.ratehawk_booking?.reason ? ` · ${cap.ratehawk_booking.reason}` : ''}</span>
          <Button size="sm" variant="outline" onClick={recheck} disabled={!!busy} data-testid="etg-recheck-btn">{busy === 'recheck' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-check</Button>
          <Button size="sm" onClick={testBooking} disabled={!!busy || !enabled} className="bg-slate-800 text-white" data-testid="etg-test-booking-btn">{busy === 'test' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} ETG test booking</Button>
        </div>
        {cap && cap.ratehawk_configured === false && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800" data-testid="etg-credentials-missing">
            RateHawk credentials are missing in this environment — add <code>RATEHAWK_KEY_ID</code> and <code>RATEHAWK_API_KEY</code> to the deployment secrets and republish, then press Re-check.
          </div>
        )}
        {cap?.ratehawk_credentials && (
          <div className="text-[11px] text-slate-500 font-mono" data-testid="etg-credentials-fingerprint">
            Using key id <span className="text-slate-800">{cap.ratehawk_credentials.key_id || '—'}</span> · API key <span className="text-slate-800">{cap.ratehawk_credentials.api_key_masked || '—'}</span> ({cap.ratehawk_credentials.api_key_len} chars) · {cap.ratehawk_credentials.base_url}
            {cap.ratehawk_credentials.has_whitespace && <span className="text-red-600"> · has stray whitespace</span>}
          </div>
        )}
        {runs.length > 0 && (
          <div className="space-y-1" data-testid="etg-test-runs">
            {runs.map((r) => (
              <div key={r.run_id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs">
                <div><span className="font-bold">{r.run_id}</span> · {r.checkin}→{r.checkout} · res {r.residency} · {(r.rooms || []).map((x) => x.order_id ? `#${x.order_id} ${x.order_info_status || x.finish_status}` : `✗ ${x.error || x.code}`).join(' | ')}</div>
                {r.cancelled ? <Badge className="bg-slate-200 text-slate-700">cancelled</Badge>
                  : <Button size="sm" variant="outline" onClick={() => cancelRun(r.run_id)} disabled={!!busy} data-testid={`etg-cancel-${r.run_id}`}>{busy === r.run_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />} Cancel</Button>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
